/*
 * Furniture & Choice commercial product cards.
 * Uses Shopify product JSON embedded by the section; no prices, SKUs or URLs are hardcoded here.
 * Created: 2026-09-08 MYT (UTC+08:00).
 * Updated: 09-09-2026 16:49 MYT — treats the first configured colour/finish product as index zero.
 */

(() => {
  if (customElements.get('fc-commercial-products')) return;

  class FCCommercialProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.querySelectorAll('[data-product-card]').forEach((card) => this.initializeCard(card));
    }

    initializeCard(card) {
      const dataNode = card.querySelector('[data-product-json]');
      if (!dataNode) return;

      let products;
      try {
        products = JSON.parse(dataNode.textContent);
      } catch (error) {
        console.error('Commercial product data could not be read.', error);
        return;
      }

      if (!Array.isArray(products) || products.length === 0) return;

      card.fcCommercialProducts = products;

      card.querySelectorAll('[data-product-index]').forEach((button, buttonIndex) => {
        button.addEventListener('click', () => {
          const productIndex = Number(button.dataset.productIndex);
          this.renderProduct(card, Number.isInteger(productIndex) ? productIndex : buttonIndex);
        });
      });

      card.addEventListener('click', (event) => {
        const mediaButton = event.target.closest('[data-media-index]');
        if (mediaButton && card.contains(mediaButton)) {
          this.selectMedia(card, mediaButton);
          return;
        }

        const variantButton = event.target.closest('[data-variant-index]');
        if (variantButton && card.contains(variantButton) && !variantButton.disabled) {
          this.selectVariant(card, Number(variantButton.dataset.variantIndex) || 0);
        }
      });

      this.renderProduct(card, 0);
    }

    renderProduct(card, productIndex) {
      const products = card.fcCommercialProducts || [];
      const product = products[productIndex] || products[0];
      if (!product) return;

      card.dataset.productIndex = String(productIndex);
      const selectedChoice = card.querySelector('[data-selected-choice]');
      const selectedChoiceButton = card.querySelector(`[data-product-index="${productIndex}"]`);
      if (selectedChoiceButton && selectedChoiceButton.dataset.productUrl) {
        product.url = selectedChoiceButton.dataset.productUrl;
      }
      if (selectedChoice) {
        selectedChoice.textContent =
          (selectedChoiceButton && selectedChoiceButton.dataset.choiceLabel) || this.choiceLabel(product.title);
      }

      card.querySelectorAll('[data-product-index]').forEach((button) => {
        button.setAttribute('aria-pressed', button === selectedChoiceButton ? 'true' : 'false');
      });

      const mainLink = card.querySelector('[data-main-image-link]');
      if (mainLink) mainLink.href = this.productUrl(product);

      this.renderMedia(card, product);
      this.renderVariants(card, product);

      const variants = Array.isArray(product.variants) ? product.variants : [];
      let variantIndex = variants.findIndex((variant) => variant.available);
      if (variantIndex < 0) variantIndex = 0;
      this.updateVariantDetails(card, product, variants[variantIndex]);
    }

    renderMedia(card, product) {
      const container = card.querySelector('[data-media-thumbnails]');
      const images = Array.isArray(product.images) ? product.images : [];
      const max = Number(card.dataset.maxMedia) || 8;
      const firstVariant = Array.isArray(product.variants)
        ? product.variants.find((variant) => variant.available) || product.variants[0]
        : null;
      const featuredImage =
        (firstVariant && firstVariant.featured_image && firstVariant.featured_image.src) ||
        product.featured_image ||
        images[0];

      if (card.querySelector('[data-product-choices]')) {
        if (featuredImage) this.updateMainImage(card, featuredImage, product.title);
        return;
      }

      if (!container) {
        if (featuredImage) this.updateMainImage(card, featuredImage, product.title);
        return;
      }

      container.replaceChildren();
      images.slice(0, max).forEach((imageUrl, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fc-commercial-product-card__thumbnail';
        button.dataset.mediaIndex = String(index);
        button.dataset.mediaUrl = imageUrl;
        button.setAttribute('aria-label', `View image ${index + 1} of ${product.title}`);
        button.setAttribute('aria-pressed', index === 0 ? 'true' : 'false');

        const image = document.createElement('img');
        image.src = this.imageUrl(imageUrl, 120);
        image.alt = '';
        image.width = 120;
        image.height = 120;
        image.loading = 'lazy';
        button.appendChild(image);
        container.appendChild(button);
      });

      container.hidden = images.length < 2;
      const firstImage = featuredImage || images[0];
      if (firstImage) this.updateMainImage(card, firstImage, product.title);
    }

    renderVariants(card, product) {
      const container = card.querySelector('[data-variant-options]');
      const variants = Array.isArray(product.variants) ? product.variants : [];
      if (!container) return;

      container.replaceChildren();
      variants.forEach((variant, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fc-commercial-product-card__variant';
        button.dataset.variantIndex = String(index);
        button.disabled = !variant.available;
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute(
          'aria-label',
          `${variant.available ? 'Select' : 'Unavailable'} ${variant.public_title || variant.title}`
        );

        const variantImage = variant.featured_image && variant.featured_image.src;
        if (variantImage) {
          const image = document.createElement('img');
          image.src = this.imageUrl(variantImage, 140);
          image.alt = '';
          image.width = 140;
          image.height = 140;
          image.loading = 'lazy';
          button.appendChild(image);
          const hiddenLabel = document.createElement('span');
          hiddenLabel.className = 'fc-commercial-visually-hidden';
          hiddenLabel.textContent = variant.public_title || variant.title;
          button.appendChild(hiddenLabel);
        } else {
          button.textContent = variant.public_title || variant.title;
        }
        container.appendChild(button);
      });

      container.hidden = variants.length < 2;
      const optionRow = card.querySelector('[data-option-row]');
      if (optionRow) optionRow.hidden = variants.length < 2;
      const options = Array.isArray(product.options) ? product.options : [];
      const label = card.querySelector('[data-option-heading]');
      if (label) {
        label.textContent = options
          .map((option) => (typeof option === 'string' ? option : option.name))
          .filter(Boolean)
          .join(' / ');
      }
    }

    selectMedia(card, button) {
      const product = this.currentProduct(card);
      if (!product) return;
      this.updateMainImage(card, button.dataset.mediaUrl, product.title);
      card.querySelectorAll('[data-media-index]').forEach((item) => {
        item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
      });
    }

    selectVariant(card, variantIndex) {
      const product = this.currentProduct(card);
      if (!product || !product.variants || !product.variants[variantIndex]) return;
      this.updateVariantDetails(card, product, product.variants[variantIndex]);
    }

    updateVariantDetails(card, product, variant) {
      if (!variant) return;

      const option = card.querySelector('[data-selected-option]');
      if (option) option.textContent = variant.public_title || variant.title || '';

      const sku = card.querySelector('[data-sku]');
      if (sku) {
        sku.textContent = variant.sku || card.dataset.emptySkuLabel || 'Not provided';
      }

      const price = card.querySelector('[data-price]');
      if (price) price.textContent = this.money(variant.price, card.dataset.currency);

      const comparePrice = card.querySelector('[data-compare-price]');
      const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (comparePrice) {
        comparePrice.hidden = !hasComparePrice;
        comparePrice.textContent = hasComparePrice
          ? this.money(variant.compare_at_price, card.dataset.currency)
          : '';
      }

      const instalment = card.querySelector('[data-instalment]');
      if (instalment) {
        const months = this.instalmentMonths(variant.price);
        const amount = Math.round((variant.price / 100 / months) * 100) / 100;
        const amountNode = instalment.querySelector('[data-instalment-amount]');
        const monthsNode = instalment.querySelector('[data-instalment-months]');
        if (amountNode) amountNode.textContent = this.money(amount * 100, card.dataset.currency, true);
        if (monthsNode) monthsNode.textContent = String(months);
      }

      const productUrl = this.productUrl(product);
      const url = productUrl === '#'
        ? '#'
        : `${productUrl}${productUrl.includes('?') ? '&' : '?'}variant=${variant.id}`;
      const cta = card.querySelector('[data-product-cta]');
      const mainLink = card.querySelector('[data-main-image-link]');
      if (cta) cta.href = url;
      if (mainLink) mainLink.href = url;

      const variantImage = variant.featured_image && variant.featured_image.src;
      if (variantImage) this.updateMainImage(card, variantImage, `${product.title}, ${variant.public_title || variant.title}`);

      card.querySelectorAll('[data-variant-index]').forEach((button, index) => {
        button.setAttribute('aria-pressed', index === product.variants.indexOf(variant) ? 'true' : 'false');
      });
    }

    updateMainImage(card, imageUrl, alt) {
      const image = card.querySelector('.fc-commercial-product-card__main-image img');
      if (!image || !imageUrl) return;
      image.src = this.imageUrl(imageUrl, 1200);
      image.removeAttribute('srcset');
      image.sizes = '(min-width: 820px) 520px, calc(100vw - 40px)';
      image.alt = alt || '';
      image.style.objectFit = 'contain';
      image.style.objectPosition = 'center';
    }

    currentProduct(card) {
      const products = card.fcCommercialProducts || [];
      return products[Number(card.dataset.productIndex) || 0] || products[0];
    }

    productUrl(product) {
      if (product && product.url) return product.url;
      if (product && product.handle) return `/products/${product.handle}`;
      return '#';
    }

    instalmentMonths(price) {
      if (price >= 150000) return 24;
      if (price >= 120000) return 12;
      if (price >= 100000) return 6;
      return 3;
    }

    choiceLabel(title) {
      const parts = String(title || '').split(',');
      return parts.length > 1 ? parts.slice(1).join(',').trim() : String(title || '');
    }

    money(cents, currency, forceDecimals = false) {
      const amount = Number(cents || 0) / 100;
      const decimals = forceDecimals || Number(cents || 0) % 100 !== 0 ? 2 : 0;
      try {
        return new Intl.NumberFormat('en-MY', {
          style: 'currency',
          currency: currency || 'MYR',
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        }).format(amount);
      } catch (error) {
        return `RM${amount.toFixed(decimals)}`;
      }
    }

    imageUrl(url, width) {
      if (!url) return '';
      const normalized = String(url).startsWith('//') ? `https:${url}` : String(url);
      const separator = normalized.includes('?') ? '&' : '?';
      return `${normalized}${separator}width=${width}`;
    }
  }

  customElements.define('fc-commercial-products', FCCommercialProducts);
})();
